import React from 'react';

export const money = (n) => '$' + Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Sin centavos — para totales grandes del dashboard (millones), donde los centavos son ruido.
export const money0 = (n) => '$' + Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 });

// Compacto para ejes/barras: $1.1M, $452k, $980.
export const moneyShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return '$' + (v / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (Math.abs(v) >= 1e3) return '$' + Math.round(v / 1e3) + 'k';
  return '$' + Math.round(v);
};

export const iniciales = (nombre = '') =>
  nombre.trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '—';

const AVA_COLORS = ['#204B7B', '#2BA7CE', '#00A86B', '#7A3FB8', '#E5631B', '#0C2036'];
export const avaColor = (s = '') => AVA_COLORS[(s.charCodeAt(0) || 0) % AVA_COLORS.length];

/** El vehículo puede venir como string o como objeto {marca, modelo, anio, placas}. */
export const vehiculoTxt = (v) => {
  if (!v) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'object') {
    return [v.marca, v.modelo, v.anio].filter(Boolean).join(' ') || '—';
  }
  return String(v);
};

export const ETAPA_LABELS = {
  preventiva: 'Preventiva', temprana: 'Temprana', administrativa: 'Administrativa',
  extrajudicial: 'Extrajudicial', judicial: 'Judicial',
  vigente: 'Al corriente', vencido: 'Vencido', deteriorado: 'Deteriorado',
  liquidado: 'Liquidado', castigado: 'Castigado',
};

/** Color por etapa de cobranza (mismos tonos que el semáforo / tokens --et-*). */
export const ETAPA_COLORS = {
  preventiva: '#2BA7CE', temprana: '#F0961E', administrativa: '#E5631B',
  extrajudicial: '#DC2626', judicial: '#991B1B',
};

export const PROD = {
  AP: { name: 'Arrendamiento puro', color: '#0E7FA6', bg: '#E4F5FB' },
  AF: { name: 'Arrendamiento financiero', color: '#21588C', bg: '#E8EFF6' },
  CS: { name: 'Crédito simple', color: '#0C2036', bg: '#E7EBF0' },
};

export const prodCorto = (tipo = '') => {
  const t = String(tipo).toLowerCase();
  if (t === 'ap' || t.includes('puro')) return 'AP';
  if (t === 'af' || t.includes('financiero')) return 'AF';
  if (t === 'cs' || t.includes('simple')) return 'CS';
  return 'AP';
};

/**
 * Estilo de etapa compartido por Estados de cuenta, Cobranza del mes y
 * Conversaciones. Acepta (etapa, estatus) o una fila completa de cobranza.
 */
export const stageStyle = (etapaOItem, estatus) => {
  const item = etapaOItem && typeof etapaOItem === 'object' ? etapaOItem : null;
  const etapa = item ? item.etapa_cobranza : etapaOItem;
  const estado = item ? (item.estatus_credito || item.estatus) : estatus;
  const tieneVencido = Number(item?.vencido || 0) > 0;
  if (etapa === 'judicial' || estado === 'castigado') return { color: '#991B1B', bg: '#FBE7E7' };
  if (etapa === 'extrajudicial' || etapa === 'administrativa' || estado === 'deteriorado') return { color: '#DC2626', bg: '#FBE7E7' };
  if (etapa === 'temprana' || estado === 'vencido' || tieneVencido) return { color: '#F0961E', bg: '#FDF1DF' };
  if (etapa === 'preventiva') return { color: '#2BA7CE', bg: '#E4F5FB' };
  if (estado === 'liquidado') return { color: '#64748B', bg: '#EEF1F4' };
  return { color: '#00BA6E', bg: '#E5F8EF' };
};

export const etapaLabelDe = (etapa, estatus) => ETAPA_LABELS[etapa] || ETAPA_LABELS[estatus] || 'Al corriente';

export const Pill = ({ icon, children, onClick, disabled, primary, waBg, title, style }) => (
  <div
    aria-disabled={disabled || undefined}
    onClick={disabled ? undefined : onClick}
    role="button"
    tabIndex={disabled ? -1 : 0}
    title={title}
    style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
      fontSize: 12.5, fontWeight: primary ? 700 : 600, padding: '9px 16px',
      borderRadius: 999, cursor: disabled ? 'default' : 'pointer',
      background: waBg || (primary ? '#22EC9A' : '#fff'),
      border: primary || waBg ? 'none' : '1px solid #E7EBF0',
      color: waBg ? '#fff' : (disabled ? '#94A3B8' : (primary ? '#06241a' : '#475569')),
      opacity: disabled ? 0.6 : 1,
      ...style,
    }}
  >
    <span className={'pi ' + icon} />{children}
  </div>
);

const TIPO_LABELS = {
  arrendamiento_puro: 'Arrendamiento puro', arrendamiento_financiero: 'Arrendamiento financiero',
  credito_simple: 'Crédito simple', AP: 'Arrendamiento puro', AF: 'Arrendamiento financiero', CS: 'Crédito simple',
};
export const tipoLabel = (t) => TIPO_LABELS[t] || t || '—';

/** Chip de etapa/estatus con punto de color. Usa etapa_cobranza si existe, si no el estatus.
 *  Si enRevision=true, muestra el chip "En revisión" (dato importado a verificar). */
export function EtapaChip({ etapa, estatus, sub, enRevision, recuperado }) {
  if (recuperado) {
    return <span className="chip chip-recuperado"><span className="pip" />Recuperado</span>;
  }
  if (enRevision) {
    return <span className="chip chip-revision"><span className="pip" />En revisión</span>;
  }
  const key = etapa || estatus || 'vigente';
  return (
    <span className={'chip chip-' + key}>
      <span className="pip" />{ETAPA_LABELS[key] || key}
      {sub ? <span style={{ opacity: 0.7, fontWeight: 600 }}> · {sub}</span> : null}
    </span>
  );
}
